// The declarative chart tree. Consumes the orientation model (so it
// can set `<ComposedChart layout>` and let every child resolve its own
// axis binding) and is fed already-shaped data + formatters by
// `CartesianPanel`. No `orientation ? … : …` ternaries live here.

import { useState } from 'react';
import { ComposedChart, Legend, ZAxis } from 'recharts';

import type { CartesianSpec } from '#core/lib/expr/ops/dashboard/value.ts';
import { chartElementColor, shadeOffsetsForColors } from '#core/lib/formatters/colors.ts';

import classes from '../CartesianPanel.module.css';
import type { LabelFormatter } from './axisLabels.tsx';
import { scatterSizeKey } from './band-keys.ts';
import { CategoryAxis } from './CategoryAxis.tsx';
import { ChartBand } from './ChartBand.tsx';
import { ChartReferenceMarkers } from './ChartReferenceMarkers.tsx';
import { ChartSeries } from './ChartSeries.tsx';
import type { ChartOrientation } from './chartOrientation.ts';
import { OrientedCartesianGrid } from './OrientedCartesianGrid.tsx';
import { OrientedTooltip } from './OrientedTooltip.tsx';
import { ValueAxis } from './ValueAxis.tsx';

// Bubble-area range (in px²) a scatter `:z` channel maps onto: the
// smallest z value paints the lower bound, the largest the upper.
const BUBBLE_AREA_RANGE = [40, 400] as const;

export function CartesianChart({
  spec,
  o,
  data,
  hasRightAxis,
  hasBar,
  stacked,
  valueTickFormatterLeft,
  valueTickFormatterRight,
  tooltipValueFormatter,
  axisTickFormatter,
  tooltipLabelFormatter,
  leftUnit,
  rightUnit,
  ticks,
  citedMarkers,
}: {
  spec: CartesianSpec;
  /** Resolved orientation model — computed once at the panel layer and threaded down. */
  o: ChartOrientation;
  data: readonly Record<string, number | string | null | readonly number[]>[];
  hasRightAxis: boolean;
  hasBar: boolean;
  stacked: boolean;
  /**
   * Formats each value-axis tick (no unit — the unit rides on the axis title).
   * A laddered axis scales every tick onto its fixed rung; otherwise each tick
   * is formatted by its own magnitude so a wide-spanning axis keeps small-tick
   * decimals while large ticks keep their thousand separator. One per side
   * because the two axes may fix different rungs.
   */
  valueTickFormatterLeft: (value: number | string | readonly (string | number)[] | undefined) => string;
  valueTickFormatterRight: (value: number | string | readonly (string | number)[] | undefined) => string;
  /** Formats tooltip values, appending the per-series declared unit by dataKey. */
  tooltipValueFormatter: (
    value: number | string | readonly (string | number)[] | undefined,
    name?: unknown,
    item?: { readonly dataKey?: string | number },
  ) => string;
  /** Sparse adaptive formatter for the category-axis tick labels. */
  axisTickFormatter: LabelFormatter;
  /** Maximally precise formatter for tooltip labels — must identify a single datapoint. */
  tooltipLabelFormatter: LabelFormatter;
  /** Declared unit suffixes for the left / right value axes. */
  leftUnit: string | undefined;
  rightUnit: string | undefined;
  /** Optional snapped tick positions for sparse continuous category axes. */
  ticks?: readonly number[];
  /** Labels of reference markers cited by insights — rendered emphasized. */
  citedMarkers?: ReadonlySet<string>;
}) {
  const [focusedSeries, setFocusedSeries] = useState<string | null>(null);
  const seriesColors = spec.series.map((s, i) => chartElementColor(s.color, i));
  const seriesShadeOffsets = shadeOffsetsForColors(seriesColors);
  // Reserve a margin strip for each rotated axis title (value Y axes, or the
  // category Y axis in horizontal layout) so the title clears its tick values.
  // A title shows when its label or its unit is non-empty.
  const reserve = o.marginReserve({
    valueLeft: Boolean(spec.yLabelLeft || leftUnit),
    valueRight: hasRightAxis && Boolean(spec.yLabelRight || rightUnit),
    category: Boolean(spec.xLabel),
  });
  return (
    <ComposedChart
      className={classes.chart}
      data={data as Record<string, number | string | null | readonly number[]>[]}
      height="100%"
      layout={o.rechartsLayout}
      margin={{ top: 20, left: reserve.left, right: reserve.right, bottom: 0 }}
      responsive
      stackOffset={spec.stack === 'percent' ? 'expand' : 'none'}
      syncId="dashboard"
      width="100%"
    >
      <OrientedCartesianGrid o={o} />
      <CategoryAxis o={o} tickFormatter={axisTickFormatter} ticks={ticks} title={spec.xLabel} />
      <ValueAxis o={o} side="left" tickFormatter={valueTickFormatterLeft} title={spec.yLabelLeft} unit={leftUnit} />
      {hasRightAxis && (
        <ValueAxis
          o={o}
          side="right"
          tickFormatter={valueTickFormatterRight}
          title={spec.yLabelRight}
          unit={rightUnit}
        />
      )}
      <Legend
        align="center"
        formatter={(value) => <span style={{ fontSize: 'var(--mantine-font-size-sm)' }}>{value}</span>}
        iconSize={8}
        onMouseOut={() => {
          setFocusedSeries(null);
        }}
        onMouseOver={(e) => {
          setFocusedSeries(e.dataKey);
        }}
        verticalAlign="bottom"
      />
      <OrientedTooltip
        hasBar={hasBar}
        labelFormatter={tooltipLabelFormatter}
        o={o}
        valueFormatter={tooltipValueFormatter}
      />
      {/* A z-axis per bubble (scatter `:z`) series, scoped by the same
          id the panel keys the z column under, so each scatter sizes its
          points from its own value range. */}
      {spec.series
        .filter((s) => s.type === 'scatter' && s.z !== undefined)
        .map((s) => (
          <ZAxis
            dataKey={scatterSizeKey(s.id)}
            key={`z-${s.id}`}
            range={[...BUBBLE_AREA_RANGE]}
            type="number"
            zAxisId={scatterSizeKey(s.id)}
          />
        ))}
      {/* Bands paint first so they sit behind every series. */}
      {spec.bands.map((b, j) => (
        <ChartBand band={b} index={j} key={`band-${b.lower.column}-${b.upper.column}-${b.label ?? ''}`} o={o} />
      ))}
      {spec.series.map((s, i) => (
        <ChartSeries
          bandCount={spec.bands.length}
          color={seriesColors[i] ?? 'blue'}
          key={s.id}
          o={o}
          opacity={focusedSeries === null || focusedSeries === s.id ? 1 : 0.25}
          series={s}
          seriesCount={spec.series.length}
          shadeOffset={seriesShadeOffsets[i] ?? 0}
          stacked={stacked}
        />
      ))}
      <ChartReferenceMarkers areas={spec.referenceAreas} cited={citedMarkers} lines={spec.referenceLines} o={o} />
    </ComposedChart>
  );
}
